/**
 * shared/auth/token — Injectable token-accessor contract.
 *
 * This module defines the interface for retrieving the current access token.
 * The real implementation (in-memory token store) lands in Phase 3 auth.
 * Phase 2 provides no-op stubs so the API client middleware can compile and
 * work for unauthenticated calls without any changes to call sites later.
 *
 * HOW TO WIRE PHASE 3:
 *   Call `setTokenAccessor(myGetToken)` once in the auth slice's init.
 *   All API requests made after that will include the Bearer token.
 *   Never store tokens in localStorage — keep them in memory only.
 */

/**
 * Signature of a function that returns the current access token string,
 * or null / undefined when no token is available (unauthenticated).
 */
export type TokenAccessor = () => string | null | undefined;

/** No-op stub — returns null (unauthenticated) until Phase 3 wires a real store. */
const noopAccessor: TokenAccessor = () => null;

/** The currently registered token accessor. Starts as the no-op stub. */
let _tokenAccessor: TokenAccessor = noopAccessor;

/**
 * Register a real token accessor.
 * Called once by the auth slice during app init (Phase 3).
 */
export function setTokenAccessor(accessor: TokenAccessor): void {
  _tokenAccessor = accessor;
}

/**
 * Get the current access token by delegating to the registered accessor.
 * Returns null when no real accessor has been registered (pre-Phase 3).
 */
export function getAccessToken(): string | null | undefined {
  return _tokenAccessor();
}

/**
 * Reset to the no-op stub.
 * Used in tests to isolate token state between test cases.
 */
export function resetTokenAccessor(): void {
  _tokenAccessor = noopAccessor;
}
